import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

/**
 * Google Ad Manager (GPT) — fresh implementation.
 *
 * Google-recommended setup:
 *  - gpt.js loaded once, async, from securepubads.g.doubleclick.net
 *  - all commands queued through googletag.cmd
 *  - Single Request Architecture (enableSingleRequest)
 *  - one defineSlot per rendered container, destroySlots on unmount
 *  - explicit refresh on SPA route change (out-of-page anchor included)
 *
 * No admin-managed code anymore. Ad unit paths are hardcoded below.
 */

const NETWORK_CODE = "22960503003";

type SlotKey = "top-banner" | "inline-1" | "inline-2" | "footer";

/** Ad Manager unit names under the network above. */
const AD_UNITS: Record<SlotKey, string> = {
  "top-banner": `/${NETWORK_CODE}/Topbanner`,
  "inline-1": `/${NETWORK_CODE}/inline2`,
  "inline-2": `/${NETWORK_CODE}/inline3`,
  footer: `/${NETWORK_CODE}/footer1`,
};

type GptSize = [number, number];

const SLOT_SIZES: Record<SlotKey, GptSize[]> = {
  "top-banner": [[728, 90], [320, 100], [320, 50], [300, 250]],
  "inline-1": [[336, 280], [300, 250], [728, 90]],
  "inline-2": [[336, 280], [300, 250], [728, 90]],
  footer: [[728, 90], [320, 100], [300, 250]],
};

/** Reserved height (CLS protection). */
const SLOT_MIN_HEIGHT: Record<SlotKey, number> = {
  "top-banner": 100,
  "inline-1": 280,
  "inline-2": 280,
  footer: 100,
};

type Placement =
  | "top-banner"
  | "inline"
  | "inline-2"
  | "mid-billboard"
  | "mid-billboard-2"
  | "related-multiplex"
  | "end-halfpage"
  | "footer";

/** Legacy placement names map onto the four live slots. */
const PLACEMENT_TO_SLOT: Record<Placement, SlotKey> = {
  "top-banner": "top-banner",
  inline: "inline-1",
  "mid-billboard": "inline-1",
  "inline-2": "inline-2",
  "mid-billboard-2": "inline-2",
  "related-multiplex": "footer",
  "end-halfpage": "footer",
  footer: "footer",
};

type GoogleTag = {
  cmd: { push: (fn: () => void) => void };
  apiReady?: boolean;
  defineSlot: (path: string, sizes: GptSize[], divId: string) => GptSlot | null;
  defineOutOfPageSlot: (path: string, format: unknown) => GptSlot | null;
  enums?: {
    OutOfPageFormat: {
      TOP_ANCHOR: unknown;
      BOTTOM_ANCHOR: unknown;
      INTERSTITIAL: unknown;
    };
  };
  pubads: () => {
    enableSingleRequest: () => void;
    collapseEmptyDivs: (arg?: boolean) => void;
    refresh: (slots?: GptSlot[]) => void;
    setCentering: (v: boolean) => void;
  };
  enableServices: () => void;
  display: (divId: string) => void;
  destroySlots: (slots?: GptSlot[]) => boolean;
  sizeMapping: () => unknown;
};

type GptSlot = {
  addService: (service: unknown) => GptSlot;
  setCollapseEmptyDiv: (a: boolean, b?: boolean) => GptSlot;
};

declare global {
  interface Window {
    googletag?: GoogleTag;
    __dh_gpt_loaded?: boolean;
    __dh_gpt_configured?: boolean;
    __dh_anchor_slot?: GptSlot | null;
    __dh_interstitial_slot?: GptSlot | null;
  }
}

/** Loads gpt.js exactly once and applies global pubads config. */
export function ensureGptScript(): void {
  if (typeof window === "undefined") return;
  window.googletag = window.googletag || ({ cmd: [] } as unknown as GoogleTag);

  if (!window.__dh_gpt_loaded) {
    window.__dh_gpt_loaded = true;
    // Performance: gpt.js is ~300KB of main-thread work. Inject it only on the
    // first user interaction or after the page has been idle for a few
    // seconds, so it never competes with LCP / TBT. Slot commands queue in
    // googletag.cmd meanwhile and run as soon as the script arrives.
    let injected = false;
    const inject = () => {
      if (injected) return;
      injected = true;
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    };
    ["pointerdown", "keydown", "scroll", "touchstart"].forEach((ev) =>
      window.addEventListener(ev, inject, { once: true, passive: true }),
    );
    const idle =
      (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
        .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1));
    window.setTimeout(() => idle(inject, { timeout: 2000 }), 3500);
  }

  window.googletag.cmd.push(() => {
    const gt = window.googletag!;
    if (window.__dh_gpt_configured) return;
    window.__dh_gpt_configured = true;
    gt.pubads().enableSingleRequest();
    // No collapseEmptyDivs(): collapsing reserved slots caused layout shift (CLS).
    gt.pubads().setCentering(true);
    gt.enableServices();
  });
}

/** Anchor + web interstitial — both are defined once for the browser session. */
export function refreshOutOfPageAds(): void {
  if (typeof window === "undefined") return;
  ensureGptScript();
  window.googletag!.cmd.push(() => {
    const gt = window.googletag!;
    try {
      // GAM web interstitials are codeless: defining the network-level slot
      // lets GPT intercept an eligible internal-link click and show the ad
      // before completing navigation. No div/display() call is required.
      if (!window.__dh_interstitial_slot) {
        const format = gt.enums?.OutOfPageFormat?.INTERSTITIAL;
        if (format !== undefined) {
          const slot = gt.defineOutOfPageSlot(`/${NETWORK_CODE}`, format);
          if (slot) {
            slot.addService(gt.pubads());
            window.__dh_interstitial_slot = slot;
          }
        }
      }

      if (!window.__dh_anchor_slot) {
        const format = gt.enums?.OutOfPageFormat?.BOTTOM_ANCHOR;
        if (format === undefined) return;
        const slot = gt.defineOutOfPageSlot(`/${NETWORK_CODE}/Anchor`, format);
        if (!slot) return;
        slot.addService(gt.pubads());
        window.__dh_anchor_slot = slot;
        gt.display(`/${NETWORK_CODE}/Anchor`);
      }
      if (window.__dh_anchor_slot) {
        gt.pubads().refresh([window.__dh_anchor_slot]);
      }
    } catch (err) {
      console.error("[GAM] anchor error", err);
    }
  });
}

let seq = 0;

/**
 * One ad per slot per page. Legacy pages call <DisplayAd/> many times; only the
 * first mount of each slot on the current route actually renders an ad unit.
 */
const claims = new Map<string, string>();
let claimPath = "";
let uidSeq = 0;

const listeners = new Set<() => void>();

function releaseSlot(slotKey: SlotKey, uid: string): void {
  if (claims.get(slotKey) === uid) {
    claims.delete(slotKey);
    listeners.forEach((fn) => fn());
  }
}

function claimSlot(pathname: string, slotKey: SlotKey, uid: string): boolean {
  if (claimPath !== pathname) {
    claimPath = pathname;
    claims.clear();
  }
  const current = claims.get(slotKey);
  if (!current || current === uid) {
    claims.set(slotKey, uid);
    return true;
  }
  return false;
}

export function DisplayAd({
  placement = "inline",
  className = "",
  sticky = false,
}: {
  placement?: Placement;
  className?: string;
  sticky?: boolean;
}) {
  const slotKey = PLACEMENT_TO_SLOT[placement] ?? "inline-1";
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [divId, setDivId] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const uidRef = useRef<string>("");
  if (!uidRef.current) uidRef.current = `ad-${(uidSeq += 1)}`;
  const [, forceRender] = useState(0);
  const owns = hydrated && claimSlot(pathname, slotKey, uidRef.current);

  useEffect(() => setHydrated(true), []);

  // A slot claimed by a component that later unmounts must become available
  // again, so the remaining placements on the page can render it.
  useEffect(() => {
    const notify = () => forceRender((n) => n + 1);
    listeners.add(notify);
    return () => {
      listeners.delete(notify);
      releaseSlot(slotKey, uidRef.current);
    };
  }, [slotKey]);

  // Fresh div id per mount / route change so GPT never reuses a stale slot.
  useEffect(() => {
    seq += 1;
    setDivId(`div-gpt-ad-${slotKey}-${seq}`);
  }, [slotKey, pathname]);

  useEffect(() => {
    if (!owns || !divId || typeof window === "undefined") return;
    if (window.location.pathname.startsWith("/admin")) return;

    ensureGptScript();
    let slot: GptSlot | null = null;
    let cancelled = false;

    window.googletag!.cmd.push(() => {
      if (cancelled) return;
      const gt = window.googletag!;
      if (!document.getElementById(divId)) {
        console.error("[GAM] missing container", divId);
        return;
      }
      try {
        slot = gt.defineSlot(AD_UNITS[slotKey], SLOT_SIZES[slotKey], divId);
        if (!slot) {
          console.error("[GAM] defineSlot returned null", AD_UNITS[slotKey], divId);
          return;
        }
        slot.addService(gt.pubads());
        gt.display(divId);
        gt.pubads().refresh([slot]);
      } catch (err) {
        console.error("[GAM] slot error", AD_UNITS[slotKey], err);
      }
    });

    return () => {
      cancelled = true;
      const gt = window.googletag;
      if (!gt || !slot) return;
      gt.cmd.push(() => {
        try {
          gt.destroySlots([slot!]);
        } catch {
          /* noop */
        }
      });
    };
  }, [divId, slotKey, owns]);

  // CLS protection: before hydration the slot is still un-claimed, so reserve
  // the exact height the ad container will occupy instead of rendering nothing.
  if (!hydrated) {
    return (
      <div
        className={`flex flex-col items-center ${className}`}
        style={{ minHeight: SLOT_MIN_HEIGHT[slotKey] }}
        aria-hidden="true"
      />
    );
  }

  if (!owns) return null;

  return (
    <div
      className={`flex flex-col items-center ${sticky ? "md:sticky md:top-20 md:z-10" : ""} ${className}`}
      data-ad-slot={slotKey}
    >
      <div
        ref={containerRef}
        id={divId ?? undefined}
        data-ad-unit={AD_UNITS[slotKey]}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          minHeight: SLOT_MIN_HEIGHT[slotKey],
          minWidth: 0,
        }}
      />
    </div>
  );
}
